import { routes } from '../../routes';
import { type Appointment, type Listing } from '../bookings/bookings-api';
import { appointmentBadge, clockOf, isUpcoming, shopTile, shortDay } from '../bookings/booking-model';
import { badgeOf, day, money, shopOf } from '../orders/order-model';
import { type Order } from '../orders/orders-api';
import { type HelpContent, type HelpQuestion, type HelpTopic, type HelpTopicId } from './help-api';

/**
 * The Help page's rules, kept apart from its markup so each is pinned by a test.
 */

/** How many orders "Which order do you need help with?" offers — the frame draws three. */
export const ORDER_ROWS = 3;
/** And how many appointments beside them. */
export const APPOINTMENT_ROWS = 2;

export interface HelpRow {
  readonly key: string;
  readonly href: string;
  readonly initials: string;
  readonly tile: string;
  readonly title: string;
  readonly line: string;
}

/** "WAITING ON PAYMENT" → "Waiting on payment": the frame writes the state in a sentence. */
export function sentence(label: string): string {
  const lower = label.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export const orderLink = (orderId: string): string =>
  `${routes.orders.path}?order=${encodeURIComponent(orderId)}`;

export const appointmentLink = (bookingId: string): string =>
  `${routes.bookings.path}/${encodeURIComponent(bookingId)}`;

/** One recent order, as the frame's row: shop, reference · day · total · state. */
export function orderRow(order: Order, now = new Date()): HelpRow {
  const shop = shopOf(order);
  return {
    key: `o:${order.id}`,
    href: orderLink(order.id),
    initials: shop.initials,
    tile: shop.tile,
    title: shop.name,
    line: [
      order.reference,
      day(order.placedAt, now),
      money(order.totalMinor, order.currency),
      sentence(badgeOf(order).label),
    ].join(' · '),
  };
}

/** One appointment: the service at the shop, when, and its state ("Requested" until the shop accepts). */
export function appointmentRow(
  appointment: Appointment,
  listing: Listing | undefined,
  now = Date.now(),
): HelpRow {
  const shop = listing?.storefront.name ?? 'The pharmacy';
  const tile = shopTile(appointment.storefrontId, shop);
  return {
    key: `a:${appointment.id}`,
    href: appointmentLink(appointment.id),
    initials: tile.initials,
    tile: tile.tile,
    title: listing?.title ?? 'Appointment',
    line: [
      shop,
      `${shortDay(appointment.startsAt, appointment.timeZone)}, ${clockOf(appointment.startsAt, appointment.timeZone)}`,
      sentence(appointmentBadge(appointment, now).label),
    ].join(' · '),
  };
}

/** The orders worth offering: the newest few. The route already answers newest first. */
export function pickOrders(orders: readonly Order[]): readonly Order[] {
  return [...orders].sort((a, b) => Date.parse(b.placedAt) - Date.parse(a.placedAt)).slice(0, ORDER_ROWS);
}

/** The appointments worth offering: the next ones still to come, else the most recent. */
export function pickAppointments(items: readonly Appointment[], now = Date.now()): readonly Appointment[] {
  const upcoming = items
    .filter((a) => isUpcoming(a, now))
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  if (upcoming.length > 0) return upcoming.slice(0, APPOINTMENT_ROWS);
  return [...items]
    .sort((a, b) => Date.parse(b.startsAt) - Date.parse(a.startsAt))
    .slice(0, APPOINTMENT_ROWS);
}

export interface SearchHit {
  readonly topic: HelpTopic;
  readonly question: HelpQuestion;
}

/** Lower-case words of two letters or more; "a" and "I" match everything and help nobody. */
function words(query: string): readonly string[] {
  return query
    .toLowerCase()
    .split(/[^a-z0-9’']+/)
    .map((word) => word.replace(/[’']/g, ''))
    .filter((word) => word.length >= 2);
}

const plain = (text: string): string => text.toLowerCase().replace(/[’']/g, '');

/** Every answer whose question, answer or topic holds every word typed, in page order. */
export function searchHelp(content: HelpContent, query: string): readonly SearchHit[] {
  const wanted = words(query);
  if (wanted.length === 0) return [];
  const hits: SearchHit[] = [];
  for (const topic of content.topics) {
    for (const question of topic.questions) {
      const haystack = plain(`${topic.label} ${question.q} ${question.a}`);
      if (wanted.every((word) => haystack.includes(word))) hits.push({ topic, question });
    }
  }
  return hits;
}

/** True when there is something to search for — two letters or more. */
export const isSearch = (query: string): boolean => words(query).length > 0;

/** `?topic=` on the URL opens that topic — from the footer, a page's "Get help", or a shared link. */
export function topicFrom(content: HelpContent, raw: string | null): HelpTopicId | undefined {
  if (raw === null) return undefined;
  return content.topics.find((topic) => topic.id === raw)?.id;
}
